// CSV export of the tender list as it is shown: the columns on offer, how a
// tender's fields become cells, and the file name. Two groups of columns:
// "list" columns come from the slim rows the page holds; "details" columns
// need each tender's full record (summary, requirements, criteria, the
// score breakdown), fetched per tender when such a column is ticked.
import { parseTag } from './tenderTags';
import type { TagKind } from './tenderTags';
import { canStillSubmit, isConcluded } from './tenderSearch';
import { csvLine } from './csv';
import { winnerNames, winnerPrices } from './tenderAward';
import type { AwardVendor } from './tenderAward';

// Shapes of the LLM-extracted criteria, as the page's Tender carries them.
export interface ExportCriterion { id?: string | null; group?: string; criterion: string; weight_pct?: number | null; sub_criteria?: ExportCriterion[]; }
export interface ExportKeyPersonnel { role: string; count?: number | null; min_experience_years?: number | null; certifications?: string[]; languages?: string[]; }
export interface ExportReference { applies_to?: string; count?: number | null; recency_years?: number | null; similarity?: string; }
export interface ExportBlocker { requirement: string; status: string; note?: string; }

// The slice of the page's Tender the export reads (structural: the page's
// Tender satisfies it). The detail fields are empty on slim list rows.
export interface ExportableTender {
    id: string;
    projectNumber?: string;
    name: string;
    lotNumber: number;
    lotTitle: string;
    summary: string;
    procurementOffice?: string;
    tags?: string[];
    publicationDate?: string | null;
    tenderStart?: string | null;
    matchingScore: number | null;
    decision?: 'pursue' | 'dismiss' | null;
    decisionReason?: string | null;
    hasDocuments: boolean;
    latestPubType?: string | null;
    ndaGated?: boolean;
    ndaRequired?: boolean;
    simapUrl?: string;
    awardVendors?: AwardVendor[] | null;
    awardDate?: string | null;
    awardSubmissions?: number | null;
    // Full record only:
    requirements?: string;
    idealProfile?: string;
    eligibilityCriteria?: ExportCriterion[] | null;
    awardCriteria?: ExportCriterion[] | null;
    keyPersonnelRequirements?: ExportKeyPersonnel[] | null;
    referenceRequirements?: ExportReference[] | null;
    matchingScoreSubScores?: Record<string, number> | null;
    matchingScoreReasons?: Record<string, string> | null;
    matchingScoreBlockers?: ExportBlocker[];
    matchingScoreConfidence?: string | null;
    matchingScoreComputedAt?: string | null;
}

export type ExportColumnGroup = 'list' | 'details';

export interface ExportColumn {
    key: string;
    label: string;
    group: ExportColumnGroup;
    cell: (tender: ExportableTender, today: Date) => string | number | null | undefined;
}

const LIST_SEPARATOR = '; ';

// The scoring dimensions, in the order and with the labels of the details
// panel's "Fit by dimension" section.
export const SCORE_DIMENSIONS: { key: string; label: string }[] = [
    { key: 'capability', label: 'Capability' },
    { key: 'experience', label: 'Experience' },
    { key: 'team', label: 'Team' },
    { key: 'size', label: 'Size fit' },
    { key: 'geography', label: 'Geography' },
];
export const SCORE_DIMENSION_LABELS: Record<string, string> =
    Object.fromEntries(SCORE_DIMENSIONS.map(d => [d.key, d.label]));

// The labels of a tender's tags of one kind, as the app shows them.
const tagLabels = (tags: string[] | undefined, kind: TagKind): string =>
    (tags ?? []).map(parseTag).filter(t => t.kind === kind).map(t => t.label).join(LIST_SEPARATOR);

// ISO date part of a timestamp or date string; blank when missing or unreadable.
const isoDate = (value: string | null | undefined): string => {
    if (!value) return '';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? '' : value.slice(0, 10);
};

// Same rule as the list rows: scores arrive as 0–1 or 0–100.
export const scorePct = (score: number | null | undefined): number | null =>
    score == null ? null : Math.round(score <= 1 ? score * 100 : score);

const statusLabel = (tender: ExportableTender, today: Date): string => {
    const pubType = tender.latestPubType ?? '';
    if (isConcluded(pubType)) return pubType === 'abandonment' ? 'Abandoned' : 'Awarded';
    return canStillSubmit(tender.tenderStart, today) ? 'Open' : 'Closed';
};

const ndaLabel = (tender: ExportableTender): string =>
    tender.ndaGated ? 'Behind NDA' : tender.ndaRequired ? 'NDA required' : '';

// "Price (40%); Quality (60%): Team (30%)"
const criterionText = (c: ExportCriterion): string => {
    const label = c.group ? `${c.id ? c.id + ': ' : '• '}${c.group === 'other_mandatory' ? '[Other mandatory] ' : ''}` : '';
    const head = label + (c.weight_pct != null ? `${c.criterion} (${c.weight_pct}%)` : c.criterion);
    const subs = (c.sub_criteria ?? []).map(criterionText).join(', ');
    return subs ? `${head}: ${subs}` : head;
};
const criteriaList = (items: ExportCriterion[] | null | undefined): string =>
    (items ?? []).map(criterionText).join(LIST_SEPARATOR);

// "1x Project lead, 5+ years, PMP, de"
const personnelText = (p: ExportKeyPersonnel): string => [
    p.count != null ? `${p.count}x ${p.role}` : p.role,
    p.min_experience_years != null ? `${p.min_experience_years}+ years` : null,
    ...(p.certifications ?? []),
    ...(p.languages ?? []),
].filter(Boolean).join(', ');

// "company: 2 references, last 5 years, comparable size"
const referenceText = (r: ExportReference): string => {
    const parts = [
        r.count != null ? `${r.count} ${r.count === 1 ? 'reference' : 'references'}` : null,
        r.recency_years != null ? `last ${r.recency_years} years` : null,
        r.similarity || null,
    ].filter(Boolean).join(', ');
    return r.applies_to ? `${r.applies_to}: ${parts}` : parts;
};

// "ISO 9001: unmet (not listed); German: met"
const blockersText = (items: ExportBlocker[] | undefined): string =>
    (items ?? []).map(b => `${b.requirement}: ${b.status}${b.note ? ` (${b.note})` : ''}`).join(LIST_SEPARATOR);

// In the order the columns appear in the file.
export const EXPORT_COLUMNS: ExportColumn[] = [
    { key: 'projectNumber', label: 'Project number', group: 'list', cell: t => t.projectNumber ?? '' },
    { key: 'name', label: 'Title', group: 'list', cell: t => t.name },
    { key: 'lotNumber', label: 'Lot no.', group: 'list', cell: t => t.lotNumber },
    { key: 'lotTitle', label: 'Lot title', group: 'list', cell: t => t.lotTitle ?? '' },
    { key: 'buyer', label: 'Buyer', group: 'list', cell: t => t.procurementOffice ?? '' },
    { key: 'canton', label: 'Canton', group: 'list', cell: t => tagLabels(t.tags, 'canton') },
    { key: 'type', label: 'Type', group: 'list', cell: t => tagLabels(t.tags, 'type') },
    { key: 'industry', label: 'Industry', group: 'list', cell: t => tagLabels(t.tags, 'cpv') },
    { key: 'topic', label: 'Topic', group: 'list', cell: t => tagLabels(t.tags, 'topic') },
    { key: 'subtopic', label: 'Sub-topic', group: 'list', cell: t => tagLabels(t.tags, 'subtopic') },
    { key: 'publicationDate', label: 'Published', group: 'list', cell: t => isoDate(t.publicationDate) },
    { key: 'deadline', label: 'Submission deadline', group: 'list', cell: t => isoDate(t.tenderStart) },
    { key: 'status', label: 'Status', group: 'list', cell: statusLabel },
    { key: 'winner', label: 'Winner', group: 'list', cell: t => winnerNames(t.awardVendors) },
    { key: 'price', label: 'Winning price', group: 'list', cell: t => winnerPrices(t.awardVendors) },
    { key: 'score', label: 'Fit %', group: 'list', cell: t => scorePct(t.matchingScore) },
    { key: 'decision', label: 'Decision', group: 'list', cell: t => t.decision ?? '' },
    { key: 'decisionReason', label: 'Decision reason', group: 'list', cell: t => t.decisionReason ?? '' },
    { key: 'documents', label: 'Documents', group: 'list', cell: t => t.hasDocuments ? 'yes' : 'no' },
    { key: 'nda', label: 'NDA', group: 'list', cell: ndaLabel },
    { key: 'simapUrl', label: 'SIMAP link', group: 'list', cell: t => t.simapUrl ?? '' },
    // Full record:
    { key: 'summary', label: 'Summary', group: 'details', cell: t => t.summary ?? '' },
    { key: 'requirements', label: 'Requirements', group: 'details', cell: t => t.requirements ?? '' },
    { key: 'idealProfile', label: 'Ideal profile', group: 'details', cell: t => t.idealProfile ?? '' },
    { key: 'eligibilityCriteria', label: 'Eligibility criteria', group: 'details', cell: t => criteriaList(t.eligibilityCriteria) },
    { key: 'awardCriteria', label: 'Award criteria', group: 'details', cell: t => criteriaList(t.awardCriteria) },
    { key: 'keyPersonnel', label: 'Key personnel', group: 'details', cell: t => (t.keyPersonnelRequirements ?? []).map(personnelText).join(LIST_SEPARATOR) },
    { key: 'references', label: 'References required', group: 'details', cell: t => (t.referenceRequirements ?? []).map(referenceText).join(LIST_SEPARATOR) },
    ...SCORE_DIMENSIONS.map(d => ({
        key: `fit_${d.key}`, label: `Fit: ${d.label}`, group: 'details' as const,
        cell: (t: ExportableTender) => scorePct(t.matchingScoreSubScores?.[d.key]),
    })),
    ...SCORE_DIMENSIONS.map(d => ({
        key: `why_${d.key}`, label: `Why: ${d.label}`, group: 'details' as const,
        cell: (t: ExportableTender) => t.matchingScoreReasons?.[d.key] ?? '',
    })),
    { key: 'blockers', label: 'Requirements check', group: 'details', cell: t => blockersText(t.matchingScoreBlockers) },
    { key: 'confidence', label: 'Score confidence', group: 'details', cell: t => t.matchingScoreConfidence ?? '' },
    { key: 'scoreDate', label: 'Scored on', group: 'details', cell: t => isoDate(t.matchingScoreComputedAt) },
    { key: 'awardDate', label: 'Award date', group: 'details', cell: t => isoDate(t.awardDate) },
    { key: 'awardSubmissions', label: 'Offers received', group: 'details', cell: t => t.awardSubmissions ?? '' },
];

export const DEFAULT_EXPORT_COLUMN_KEYS: string[] = EXPORT_COLUMNS.filter(c => c.group === 'list').map(c => c.key);
export const DETAIL_COLUMN_KEYS: string[] = EXPORT_COLUMNS.filter(c => c.group === 'details').map(c => c.key);

export function needsDetails(columnKeys: string[]): boolean {
    return columnKeys.some(k => DETAIL_COLUMN_KEYS.includes(k));
}

// Header + one line per tender, "\n"-separated, without the BOM.
export function tenderCsv(tenders: ExportableTender[], columnKeys: string[], today: Date): string {
    const wanted = new Set(columnKeys);
    const columns = EXPORT_COLUMNS.filter(c => wanted.has(c.key));
    const lines = [csvLine(columns.map(c => c.label))];
    for (const tender of tenders) {
        lines.push(csvLine(columns.map(c => c.cell(tender, today))));
    }
    return lines.join('\n');
}

export function exportFilename(now: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `tenders_${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
}

// Loads one record per id, at most `concurrency` at a time, in the ids'
// order; a failed load is null so the export still goes out with blank
// cells for that row. onProgress gets the number done so far.
export async function loadInBatches<T>(
    ids: string[],
    load: (id: string) => Promise<T>,
    concurrency: number,
    onProgress?: (done: number) => void,
): Promise<(T | null)[]> {
    const results: (T | null)[] = new Array(ids.length).fill(null);
    let next = 0;
    let done = 0;
    const worker = async () => {
        while (next < ids.length) {
            const i = next++;
            try {
                results[i] = await load(ids[i]);
            } catch {
                results[i] = null;
            }
            done++;
            onProgress?.(done);
        }
    };
    await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, ids.length)) }, worker));
    return results;
}
